import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { AnalyticsClipStat, AnalyticsCohort, AnalyticsRecentUpload } from '@proref/types';
import { useAuth } from '../../contexts/auth-context';
import { useAnalyticsOverview } from '../../hooks/use-analytics';
import { PageHeader } from '../../components/shared/PageHeader';
import { Card, Legend } from '../../components/shared/Card';
import { Skeleton } from '../../components/shared/Skeleton';
import { pct, timeAgo } from '../../lib/format';
import { AnalyticsKpiCards } from '../../components/analytics/AnalyticsKpiCards';
import { EngagementChart } from '../../components/analytics/EngagementChart';
import { RuleWeaknessHeatmap } from '../../components/analytics/RuleWeaknessHeatmap';
import { AnalyticsTabs } from '../../components/analytics/AnalyticsTabs';

// The federation-admin Analytics dashboard (§11), recreated from the RefSphere design with live,
// federation-scoped data. super_admin has no federation, so the API returns the zeroed shape and the
// page renders empty states rather than 403-ing. Fetch failures render inline (never a toast) per
// our data-fetch convention.
export default function AnalyticsPage() {
  const { t } = useTranslation();
  const { role } = useAuth();
  // Only the two admin roles can reach the route, but a super_admin's board is intentionally empty
  // (their platform-wide view is a later follow-up); surface that instead of a blank grid.
  const isSuperAdmin = role === 'super_admin';

  const { data, isLoading, isError, refetch } = useAnalyticsOverview();
  const overview = data?.overview;

  return (
    <div>
      <PageHeader title={t('admin.analytics.dashboard.title')} sub={t('admin.analytics.dashboard.sub')} />
      <AnalyticsTabs />

      {isError && !data ? (
        <div className="rounded-lg border border-dashed border-danger bg-danger-tint px-6 py-12 text-center">
          <p className="text-sm text-danger">{t('admin.analytics.dashboard.couldNotLoad')}</p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="mt-2 text-xs font-medium text-ink underline underline-offset-2 hover:text-ink-2"
          >
            {t('common.tryAgain')}
          </button>
        </div>
      ) : (
        <div className="space-y-5">
          {isSuperAdmin && (
            <div className="rounded border border-line bg-surface-2 px-4 py-3 text-sm text-muted">
              {t('admin.analytics.dashboard.superAdminNote')}
            </div>
          )}

          <AnalyticsKpiCards kpis={overview?.kpis} accuracyTarget={data?.accuracyTarget ?? 0.75} loading={isLoading} />

          {/* Engagement (wide) + rule weakness heatmap. items-start: without it, grid's default
              stretch forces the shorter card to match the taller one's height (the heatmap grows
              with the rulebook's article count), leaving dead space below the fixed-height chart. */}
          <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[1.4fr_1fr]">
            <Card
              title={t('admin.analytics.dashboard.engagementTitle')}
              sub={t('admin.analytics.dashboard.engagementSub')}
              actions={
                <div className="flex gap-3.5">
                  <Legend dot="var(--ink)" label={t('admin.analytics.dashboard.legendActive')} />
                  <Legend dot="var(--accent)" label={t('admin.analytics.dashboard.legendCompletions')} />
                </div>
              }
            >
              {isLoading || !overview ? <Skeleton className="h-[180px] w-full" /> : <EngagementChart data={overview.engagement} />}
            </Card>

            <Card title={t('admin.analytics.dashboard.cohortWeaknessTitle')} sub={t('admin.analytics.dashboard.cohortWeaknessSub')}>
              {isLoading || !overview ? <Skeleton className="h-[180px] w-full" /> : <RuleWeaknessHeatmap rules={overview.rule_weakness} />}
            </Card>
          </div>

          {/* Top clips · contested clips · cohort progress. */}
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            <Card title={t('admin.analytics.dashboard.topClipsTitle')} sub={t('admin.analytics.dashboard.topClipsSub')}>
              <ClipList clips={overview?.top_clips} loading={isLoading} emptyLabel={t('admin.analytics.dashboard.notEnoughAttempts')} />
            </Card>
            <Card title={t('admin.analytics.dashboard.contestedClipsTitle')} sub={t('admin.analytics.dashboard.contestedClipsSub')}>
              <ClipList
                clips={overview?.contested_clips}
                loading={isLoading}
                emptyLabel={t('admin.analytics.dashboard.notEnoughAttempts')}
                warn
              />
            </Card>
            <Card title={t('admin.analytics.dashboard.cohortProgressTitle')} sub={t('admin.analytics.dashboard.cohortProgressSub')}>
              <CohortList cohorts={overview?.cohorts} loading={isLoading} />
            </Card>
          </div>

          {/* Recent uploads / moderation. */}
          <Card title={t('admin.analytics.dashboard.recentUploadsTitle')} sub={t('admin.analytics.dashboard.recentUploadsSub')}>
            <RecentUploads uploads={overview?.recent_uploads} loading={isLoading} />
          </Card>
        </div>
      )}
    </div>
  );
}

// ─── list/table sections ─────────────────────────────────────────────────────

function ClipList({
  clips,
  loading,
  emptyLabel,
  warn,
}: {
  clips: AnalyticsClipStat[] | undefined;
  loading: boolean;
  emptyLabel: string;
  warn?: boolean;
}) {
  const { t } = useTranslation();
  if (loading || !clips) return <ListSkeleton rows={5} />;
  if (clips.length === 0) return <p className="text-sm text-muted">{emptyLabel}</p>;
  return (
    <div className="space-y-2">
      {clips.map((c, i) => (
        <div key={c.id} className="flex items-center gap-2.5">
          <span className="w-5 font-mono text-[11px] text-muted">{i + 1}</span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[12.5px] text-ink">{c.title}</div>
            <div className="text-[11px] text-muted">
              {c.article_number ? t('common.ruleLabel', { number: c.article_number }) : t('common.untagged')} ·{' '}
              {t('admin.analytics.dashboard.attemptsSuffix', { count: c.attempts })}
            </div>
          </div>
          <span className={`font-mono text-sm font-semibold ${warn ? 'text-warn' : 'text-ink'}`}>{pct(c.accuracy)}</span>
        </div>
      ))}
    </div>
  );
}

function CohortList({ cohorts, loading }: { cohorts: AnalyticsCohort[] | undefined; loading: boolean }) {
  const { t } = useTranslation();
  if (loading || !cohorts) return <ListSkeleton rows={4} />;
  if (cohorts.length === 0) return <p className="text-sm text-muted">{t('admin.analytics.dashboard.noReferees')}</p>;
  return (
    <div className="space-y-3">
      {cohorts.map((c) => {
        const p = c.avg_accuracy ?? 0;
        // Bar tone mirrors the heatmap thresholds so a "weak" cohort reads consistently across cards.
        const tone = p < 0.6 ? 'bg-danger' : p < 0.8 ? 'bg-accent' : 'bg-success';
        return (
          <div key={c.licence_level}>
            <div className="mb-1.5 flex justify-between">
              <span className="text-[12.5px] font-medium text-ink">{c.licence_level}</span>
              <span className="font-mono text-[11px] text-muted">
                {c.referees} · {pct(c.avg_accuracy)}
              </span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-line-2">
              <div className={`h-full rounded-full ${tone} transition-all`} style={{ width: `${Math.round(p * 100)}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

const STATUS_CHIP: Record<string, string> = {
  live: 'bg-success-tint text-success',
  pending: 'bg-warn-tint text-warn',
  failed: 'bg-danger-tint text-danger',
};

function RecentUploads({ uploads, loading }: { uploads: AnalyticsRecentUpload[] | undefined; loading: boolean }) {
  const { t } = useTranslation();
  if (loading || !uploads) return <ListSkeleton rows={5} />;
  if (uploads.length === 0) return <p className="text-sm text-muted">{t('admin.analytics.dashboard.noUploads')}</p>;
  const STATUS_LABEL: Record<string, string> = {
    live: t('admin.analytics.dashboard.statusLive'),
    pending: t('admin.analytics.dashboard.statusPending'),
    failed: t('admin.analytics.dashboard.statusFailed'),
  };
  return (
    <div className="overflow-x-auto">
      {/* min-width, not just the scroller: `w-full` on its own crushes the clip title instead of
          giving the container something to scroll. */}
      <table className="w-full min-w-[560px] border-collapse text-[13px]">
        <thead>
          <tr>
            {[
              t('admin.analytics.dashboard.colClip'),
              t('admin.clips.review.ruleLabel'),
              t('admin.analytics.dashboard.colWhen'),
              t('admin.analytics.dashboard.colViews'),
              t('admin.exams.list.colStatus'),
            ].map((h) => (
              <th
                key={h}
                className="border-b border-line px-3 py-2 text-left text-[10.5px] font-semibold uppercase tracking-wider text-muted"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {uploads.map((u) => {
            // "Live" once published; otherwise still in the ready/processing pipeline → "Pending".
            const label = u.published ? 'live' : u.status === 'failed' ? 'failed' : 'pending';
            return (
              <tr key={u.id} className="border-b border-line">
                <td className="px-3 py-2.5">
                  <div className="truncate font-medium text-ink">{u.title}</div>
                  {u.uploaded_by && <div className="text-[11px] text-muted">{u.uploaded_by}</div>}
                </td>
                <td className="px-3 py-2.5 font-mono text-muted">{u.article_number ?? '—'}</td>
                <td className="px-3 py-2.5 text-muted">{timeAgo(u.uploaded_at)}</td>
                <td className="px-3 py-2.5 font-mono">{u.views.toLocaleString('en-US')}</td>
                <td className="px-3 py-2.5">
                  <span
                    className={`inline-flex h-[22px] items-center rounded-full px-2.5 text-[11px] font-medium capitalize ${STATUS_CHIP[label]}`}
                  >
                    {STATUS_LABEL[label]}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ListSkeleton({ rows }: { rows: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-8 w-full" />
      ))}
    </div>
  );
}

